import { createSlice } from "@reduxjs/toolkit";
import {
  getChatGroupsList,
  getSupportTypes,
  clearStreamChatState,
} from "../actions/streamChatActions";

const initialState = {
  byChannelId: {},
  bySupportTypeId: {},
  // Monotonic "last activity" timestamp per channel, bumped when a new message
  // arrives in real time. Chat lists / sliders sort by this (desc) so a chat
  // that just received a message jumps to the top; channels with no live
  // activity keep 0 and thus retain their backend order under a stable sort.
  lastActivityById: {},
  // Channels/support-types the user just opened (marked read locally). The
  // backend list endpoint caches unread for ~5 min, so a list re-fetch right
  // after reading would otherwise resurrect the stale count. While an id is
  // flagged here we force its unread to 0 on re-fetch, until a genuinely new
  // message arrives (which clears the flag).
  recentlyReadIds: {},
  recentlyReadSupportIds: {},
  // Authoritative total unread reported by Stream Chat in real time
  // (`event.total_unread_count`). null = not yet known (fall back to summed counts).
  streamTotal: null,
};

const sanitizeCount = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return 0;
  }

  return Math.max(0, parsed);
};

const chatUnreadSlice = createSlice({
  name: "chatUnread",
  initialState,
  reducers: {
    setChannelUnread: (state, action) => {
      const id = String(action.payload?.id || "").trim();
      if (!id) return;
      state.byChannelId[id] = sanitizeCount(action.payload?.count);
    },
    incrementChannelUnread: (state, action) => {
      const id = String(action.payload?.id || "").trim();
      if (!id) return;
      const step = sanitizeCount(action.payload?.by ?? 1) || 1;
      const current = sanitizeCount(state.byChannelId[id]);
      state.byChannelId[id] = current + step;
      // A genuinely new message arrived → this channel is no longer "just read".
      if (state.recentlyReadIds) delete state.recentlyReadIds[id];
    },
    markChannelRead: (state, action) => {
      const id = String(action.payload?.id || "").trim();
      if (!id) return;
      state.byChannelId[id] = 0;
      if (!state.recentlyReadIds) state.recentlyReadIds = {};
      state.recentlyReadIds[id] = true;
    },
    // Record real-time activity so the chat lists can float this channel to the
    // top. `at` is a caller-supplied timestamp (kept out of the reducer to stay
    // pure). Bumped for every incoming message, including brand-new chats not
    // yet in the list (they surface at the top once the list refresh lands).
    bumpChannelActivity: (state, action) => {
      const id = String(action.payload?.id || "").trim();
      if (!id) return;
      if (!state.lastActivityById) state.lastActivityById = {};
      const at = Number(action.payload?.at);
      state.lastActivityById[id] = Number.isFinite(at) ? at : Date.now();
    },
    setSupportTypeUnread: (state, action) => {
      const id = String(action.payload?.id || "").trim();
      if (!id) return;
      state.bySupportTypeId[id] = sanitizeCount(action.payload?.count);
    },
    markSupportTypeRead: (state, action) => {
      const id = String(action.payload?.id || "").trim();
      if (!id) return;
      state.bySupportTypeId[id] = 0;
      if (!state.recentlyReadSupportIds) state.recentlyReadSupportIds = {};
      state.recentlyReadSupportIds[id] = true;
    },
    setStreamTotalUnread: (state, action) => {
      const value = Number(action.payload);
      state.streamTotal = Number.isFinite(value) ? Math.max(0, value) : null;
    },
    resetUnread: () => ({ ...initialState }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(getChatGroupsList.fulfilled, (state, action) => {
        const groups = action.payload?.groups || [];
        if (!state.recentlyReadIds) state.recentlyReadIds = {};
        groups.forEach((group) => {
          const id = String(group?.providerGroupId || group?.id || "").trim();
          if (!id) return;
          const fetched = sanitizeCount(group?.unreadCount);
          if (state.recentlyReadIds[id]) {
            // Keep it read — ignore the backend's (possibly cached) stale count.
            state.byChannelId[id] = 0;
            // Backend has caught up to 0 → flag no longer needed.
            if (fetched === 0) delete state.recentlyReadIds[id];
          } else {
            // A list refresh must never clear a badge merely because Stream or
            // the backend currently reports 0. Locally persisted unread stays
            // visible until this exact channel is opened and markChannelRead is
            // dispatched. Positive server counts can still increase it.
            state.byChannelId[id] = Math.max(
              sanitizeCount(state.byChannelId[id]),
              fetched,
            );
          }
        });
      })
      .addCase(getSupportTypes.fulfilled, (state, action) => {
        const supportTypes = action.payload?.supportTypes || [];
        if (!state.recentlyReadSupportIds) state.recentlyReadSupportIds = {};
        supportTypes.forEach((supportType) => {
          const id = String(supportType?.id || "").trim();
          if (!id) return;
          const fetched = sanitizeCount(supportType?.unreadCount);
          if (state.recentlyReadSupportIds[id]) {
            state.bySupportTypeId[id] = 0;
            if (fetched === 0) delete state.recentlyReadSupportIds[id];
          } else {
            state.bySupportTypeId[id] = Math.max(
              sanitizeCount(state.bySupportTypeId[id]),
              fetched,
            );
          }
        });
      })
      .addCase(clearStreamChatState.fulfilled, () => ({ ...initialState }));
  },
});

export const {
  setChannelUnread,
  incrementChannelUnread,
  markChannelRead,
  bumpChannelActivity,
  setSupportTypeUnread,
  markSupportTypeRead,
  setStreamTotalUnread,
  resetUnread,
} = chatUnreadSlice.actions;

// Map of channelId -> last real-time activity timestamp. Consumers memoize a
// sorted view of their channel list against this (stable sort keeps the
// backend order for channels with no live activity).
const EMPTY_CHANNEL_ACTIVITY_MAP = {};
export const selectChannelActivityMap = (state) =>
  state.chatUnread?.lastActivityById || EMPTY_CHANNEL_ACTIVITY_MAP;

export const selectChannelUnread = (channelId) => (state) => {
  const id = String(channelId || "").trim();
  if (!id) return 0;
  return sanitizeCount(state.chatUnread?.byChannelId?.[id]);
};

export const selectSupportTypeUnread = (supportTypeId) => (state) => {
  const id = String(supportTypeId || "").trim();
  if (!id) return 0;
  return sanitizeCount(state.chatUnread?.bySupportTypeId?.[id]);
};

export const selectTotalUnread = (state) => {
  // Sum unread ONLY over the chats the app actually surfaces (the curated
  // lists populate `byChannelId`; support types populate `bySupportTypeId`).
  // We intentionally do NOT use Stream's global `total_unread_count`: it counts
  // every channel the user is a member of — including hidden/stale chats (e.g.
  // a `chat_free` chat left over from a past trial, or the non-assigned one of
  // the two alternating free chats) that never appear in the chat list. Those
  // are never added to `byChannelId`, so they contribute 0 here ("cleared").
  const channelTotal = Object.values(state.chatUnread?.byChannelId || {}).reduce(
    (sum, count) => sum + sanitizeCount(count),
    0,
  );
  const supportTotal = Object.values(
    state.chatUnread?.bySupportTypeId || {},
  ).reduce((sum, count) => sum + sanitizeCount(count), 0);
  return channelTotal + supportTotal;
};

export default chatUnreadSlice.reducer;
